import { Injectable, Signal, WritableSignal, computed, effect, inject, signal } from '@angular/core';

import {
    CONFIG_CONSTRAINTS,
    DEFAULT_RAILING,
    DEFAULT_STAIR_CONFIG,
    IRailingConfig,
    IStairCalculationResult,
    IStairConfig,
    IStairType,
    LastStairConfigService,
    buildInitialConfig,
    defaultTurnAtStep,
    railingMaterialsOf,
} from '@stair-craft/common/util';

import { autoTurnAtStep, normalize } from './config-normalization';
import { GeometryEngineService } from './geometry-engine.service';

interface IOpeningSnapshot {
    openingLength: number;
    openingWidth: number;
    openingX: number;
    openingZ: number;
    openingLocked: boolean;
}

interface ISpiralOpeningSnapshot extends IOpeningSnapshot {
    spiralDiameter: number | null;
    spiralRotation: number | null;
}

type IOpeningRectSnapshot = Pick<IOpeningSnapshot, 'openingLength' | 'openingWidth' | 'openingX' | 'openingZ'>;

/**
 * Openings the constructor opened straight, L and U stairs with before: 3500 × 900, 3000 × 2000
 * and 3000 × 2500 on the site, then 3400 × 2500 and 3700 × 2500 while the headroom and the L-shape
 * flight 2 were tuned. A returning visitor's saved config carries one of them although they never
 * touched it, and the straight stair under a 3000 mm opening has a headroom error and red steps.
 */
const RETIRED_DEFAULT_OPENINGS: readonly IOpeningRectSnapshot[] = [
    { openingLength: 3500, openingWidth: 900, openingX: 0, openingZ: 3000 },
    { openingLength: 3000, openingWidth: 2000, openingX: 0, openingZ: 3500 },
    { openingLength: 3000, openingWidth: 2500, openingX: 0, openingZ: 3500 },
    { openingLength: 3400, openingWidth: 2500, openingX: 0, openingZ: 3100 },
    { openingLength: 3700, openingWidth: 2500, openingX: 0, openingZ: 2800 },
];

/**
 * Whether a saved non-spiral opening is exactly one of the former defaults: then the visitor never
 * changed it, and the current default replaces it. Any other opening is the visitor's own and stays.
 */
export function isRetiredDefaultOpening(config: IOpeningRectSnapshot): boolean {
    return RETIRED_DEFAULT_OPENINGS.some(
        (retired: IOpeningRectSnapshot) =>
            retired.openingLength === config.openingLength &&
            retired.openingWidth === config.openingWidth &&
            retired.openingX === config.openingX &&
            retired.openingZ === config.openingZ
    );
}

/** The saved opening carried into a fresh page: the same spiral/non-spiral bucket, and not a former default. */
function openingFromLast(last: IStairConfig, initialType: IStairType): Partial<IStairConfig> {
    const sameBucket: boolean = (last.type === 'spiral') === (initialType === 'spiral');
    if (!sameBucket || (last.type !== 'spiral' && isRetiredDefaultOpening(last))) {
        return {};
    }
    return {
        openingLength: last.openingLength,
        openingWidth: last.openingWidth,
        openingX: last.openingX,
        openingZ: last.openingZ,
        openingLocked: last.openingLocked,
    };
}

function snapshotOpening(config: IStairConfig): IOpeningSnapshot {
    return {
        openingLength: config.openingLength,
        openingWidth: config.openingWidth,
        openingX: config.openingX,
        openingZ: config.openingZ,
        openingLocked: config.openingLocked,
    };
}

function applySpiralDefaults(config: IStairConfig): IStairConfig {
    const sp: typeof CONFIG_CONSTRAINTS.spiral = CONFIG_CONSTRAINTS.spiral;
    const size: number = sp.defaultDiameter + sp.openingMargin;
    return {
        ...config,
        spiralDiameter: sp.defaultDiameter,
        spiralRotation: null,
        openingLength: size,
        openingWidth: size,
        openingX: 0,
        openingZ: Math.max(0, config.roomLength - size),
    };
}

function applyNonSpiralDefaults(config: IStairConfig): IStairConfig {
    const len: number = DEFAULT_STAIR_CONFIG.openingLength;
    const wid: number = DEFAULT_STAIR_CONFIG.openingWidth;
    return {
        ...config,
        openingLength: len,
        openingWidth: wid,
        openingX: 0,
        openingZ: Math.max(0, config.roomLength - len),
        openingLocked: DEFAULT_STAIR_CONFIG.openingLocked,
    };
}

/**
 * On a stair-type change, clamp the angle to the new type's range and set the
 * turn-at-step default: L/U take their type's default for the turn type when unset
 * (`defaultTurnAtStep`); straight/spiral clear it.
 * Extracted from `applyConfig` to keep its complexity low.
 */
function clampAngleAndTurn(config: IStairConfig): IStairConfig {
    const lu: boolean = config.type === 'l-shape' || config.type === 'u-shape';
    const angleRange: { min: number; max: number } = lu ? CONFIG_CONSTRAINTS.stair.angleLUShape : CONFIG_CONSTRAINTS.stair.angle;
    const updates: Partial<IStairConfig> = {};
    if (config.stairAngle != null) {
        updates.stairAngle = Math.max(angleRange.min, Math.min(angleRange.max, config.stairAngle));
    }
    if (lu && config.turnAtStep == null) {
        updates.turnAtStep = defaultTurnAtStep(config.type, config.turnType);
    }
    if (!lu) {
        updates.turnAtStep = null;
    }
    return { ...config, ...updates };
}

/**
 * Re-pick an untouched L/U turn step (still the type's default) for the config's own room,
 * floor and opening, so the stair opens in norm there and not only at the defaults.
 */
function settleDefaultTurnStep(config: IStairConfig): IStairConfig {
    if (config.turnAtStep !== defaultTurnAtStep(config.type, config.turnType)) {
        return config;
    }
    return { ...config, turnAtStep: autoTurnAtStep(config) };
}

/**
 * Restore the incoming type's opening from its bucket cache, or apply the type's
 * defaults when nothing was cached. Extracted from `applyConfig`.
 */
function restoreOpeningForType(
    config: IStairConfig,
    cache: { nonSpiral: IOpeningSnapshot | null; spiral: ISpiralOpeningSnapshot | null }
): IStairConfig {
    if (config.type === 'spiral') {
        const cached: ISpiralOpeningSnapshot | null = cache.spiral;
        return cached
            ? {
                  ...config,
                  openingLength: cached.openingLength,
                  openingWidth: cached.openingWidth,
                  openingX: cached.openingX,
                  openingZ: cached.openingZ,
                  openingLocked: cached.openingLocked,
                  spiralDiameter: cached.spiralDiameter,
                  spiralRotation: cached.spiralRotation,
              }
            : applySpiralDefaults(config);
    }
    const cached: IOpeningSnapshot | null = cache.nonSpiral;
    return cached
        ? {
              ...config,
              openingLength: cached.openingLength,
              openingWidth: cached.openingWidth,
              openingX: cached.openingX,
              openingZ: cached.openingZ,
              openingLocked: cached.openingLocked,
          }
        : applyNonSpiralDefaults(config);
}

/**
 * Owns the live stair configuration for the constructor page: the single source
 * of truth signal, the stair-type transition rules (angle clamp, turn defaults,
 * opening carry-over between spiral / non-spiral buckets), the derived
 * calculation result, and localStorage persistence.
 *
 * Provided per constructor-page instance (`providers: [StairConfigSessionService]`),
 * so its state and the persistence effect live and die with the page. The page
 * keeps the view-level concerns it can't own here — URL reflection, analytics,
 * and router wiring.
 */
@Injectable()
export class StairConfigSessionService {
    readonly #engine: GeometryEngineService = inject(GeometryEngineService);
    readonly #lastConfigStore: LastStairConfigService = inject(LastStairConfigService);

    readonly #config: WritableSignal<IStairConfig> = signal<IStairConfig>(DEFAULT_STAIR_CONFIG);

    /** Carried-over opening dimensions per bucket, so switching type and back restores the user's opening. */
    readonly #openingCache: { nonSpiral: IOpeningSnapshot | null; spiral: ISpiralOpeningSnapshot | null } = {
        nonSpiral: null,
        spiral: null,
    };

    public readonly config: Signal<IStairConfig> = this.#config.asReadonly();

    readonly #calcResult: Signal<{ result: IStairCalculationResult | null; error: string | null }> = computed(() => {
        try {
            return { result: this.#engine.calculate(this.#config()), error: null };
        } catch (e: unknown) {
            return { result: null, error: (e as Error).message || $localize`:@@errorCalculation:Ошибка расчёта` };
        }
    });

    public readonly result: Signal<IStairCalculationResult | null> = computed(() => this.#calcResult().result);
    public readonly error: Signal<string | null> = computed(() => this.#calcResult().error);

    constructor() {
        // Persist the current config so the home contact form can attach it to outgoing
        // emails after the user navigates away from the constructor. SSR-safe: the store
        // skips localStorage off-browser.
        effect((): void => {
            this.#lastConfigStore.set(this.#config());
        });
    }

    /**
     * Build the starting config from the requested stair type, the persisted last
     * config (room always; opening only within the same spiral/non-spiral bucket),
     * and a partial decoded from the URL. Mirrors the precedence the constructor
     * page applied inline before.
     */
    public initialize(initialType: IStairType, urlPartial: Partial<IStairConfig>): void {
        const base: IStairConfig = buildInitialConfig(initialType);
        const last: IStairConfig | null = this.#lastConfigStore.config();
        const sharedFromLast: Partial<IStairConfig> = last
            ? {
                  roomLength: last.roomLength,
                  roomWidth: last.roomWidth,
                  floorHeight: last.floorHeight,
                  floorThickness: last.floorThickness,
                  ...openingFromLast(last, initialType),
              }
            : {};
        const withUrl: IStairConfig = {
            ...base,
            ...sharedFromLast,
            ...urlPartial,
            railing: { ...DEFAULT_RAILING, ...(urlPartial.railing as Partial<IRailingConfig> | undefined) },
        };
        // A link made before the railing had species of its own opens it in the treads' wood.
        const merged: IStairConfig = { ...withUrl, ...railingMaterialsOf(urlPartial, withUrl.treadMaterialId) };
        // The restored room and floor may differ from the defaults the type's turn step was
        // chosen for: an untouched turn step is re-picked for them, a URL one is kept.
        this.#config.set(urlPartial.turnAtStep === undefined ? settleDefaultTurnStep(merged) : merged);
    }

    /**
     * Accept a new config. When the stair type changed, clamp the angle to the new
     * type's range, default/clear `turnAtStep`, snapshot the outgoing opening into
     * its bucket, and restore (or default) the opening for the incoming type.
     * Returns the type-change so the caller can fire its own analytics.
     */
    public applyConfig(newConfig: IStairConfig): { typeChanged: boolean; from: IStairType; to: IStairType } {
        const prev: IStairConfig = this.#config();
        const typeChanged: boolean = newConfig.type !== prev.type;

        if (typeChanged) {
            newConfig = clampAngleAndTurn(newConfig);
            this.#snapshotOutgoingOpening(prev);
            newConfig = settleDefaultTurnStep(restoreOpeningForType(newConfig, this.#openingCache));
        }

        // On a same-type edit, settle the geometric invariants through the single
        // normalization seam. A type change already produced a valid config (angle
        // clamp, opening restore/default), so it is trusted as-is.
        const settled: IStairConfig = typeChanged ? newConfig : normalize(prev, newConfig);

        this.#config.set(settled);
        return { typeChanged, from: prev.type, to: settled.type };
    }

    /** Reset parameters to defaults for the current stair type, clearing the opening cache. */
    public reset(): void {
        this.#openingCache.nonSpiral = null;
        this.#openingCache.spiral = null;
        this.#config.set(buildInitialConfig(this.#config().type));
    }

    /** Move the stairwell opening to a new position (from a viewport drag). */
    public applyOpeningDrag(openingX: number, openingZ: number): void {
        const c: IStairConfig = this.#config();
        this.#config.set(normalize(c, { ...c, openingX, openingZ, railing: { ...c.railing } }));
    }

    /** Snapshot the outgoing type's opening into its bucket cache, so a later switch back restores it. */
    #snapshotOutgoingOpening(prev: IStairConfig): void {
        if (prev.type === 'spiral') {
            this.#openingCache.spiral = {
                ...snapshotOpening(prev),
                spiralDiameter: prev.spiralDiameter,
                spiralRotation: prev.spiralRotation,
            };
        } else {
            this.#openingCache.nonSpiral = snapshotOpening(prev);
        }
    }
}
